import {
  IconDropDownButton,
  InPlaceHeaderEditor,
} from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBoardProvider } from "../../contexts/board/boardContext";
import { useUpdateView } from "../../data/mutations/viewMutations";
import { useView } from "../../data/queries/viewQueries";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import { useEvent } from "../../hooks/useEvent";
import useModalEvent from "../../hooks/useModalEvent";

const ViewTitle = ({
  viewId,
  viewName,
  allowRenaming = false,
  isFavoriteView = false,
}) => {
  const { data: view } = useView(viewId);
  const { t, ready } = useTranslation(["Layout", "Shared"], {
    useSuspense: false,
  });
  const { Events, useRegisterCustomEvent } = useEvent();
  const { ModalEvents, toggleModal } = useModalEvent();
  const { mutate: updateView } = useUpdateView();
  const { currentBoardId } = useBoardProvider();
  const { UserId } = useDueDatesAuth();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [results, setResults] = useState(0);

  const viewNameRef = useRef(null);

  const ids = {
    favoriteButton: "dd_view-title_favorite-button",
    nameEditor: "dd_view-title_inline-name-editor",
    viewOptionsDropdown: "dd_view-title_view-options-menu-trigger-button",
    viewOptions: {
      rename: `dd_view-title_view-options-menu_rename`,
      favorite: `dd_view-title_view-options-menu_favorite`,
      delete: `dd_view-title_view-options-menu_delete`,
    },
  };

  useRegisterCustomEvent(Events.SetTaskGridCount, (e) => {
    setResults(e?.detail?.count ?? 0);
  });

  const handleFavorite = () => {
    const updatedView = {
      Id: view?.Id,
      Name: view?.Name,
      IsFavorite: !view?.IsFavorite,
      Order: view?.Order,
      UserId: UserId,
      BoardId: currentBoardId,
    };

    updateView(
      { view: updatedView },
      {
        onSuccess: (res) => {
          const { UpdatedEntity } = res;
          queryClient.invalidateQueries(["get-view", UpdatedEntity.Id]);
          queryClient.setQueryData(
            ["get-views", UpdatedEntity.BoardId],
            (old) => {
              const updatedViews = old?.map((v) => {
                if (v.Id === UpdatedEntity.Id) {
                  return {
                    ...v,
                    IsFavorite: updatedView?.IsFavorite,
                  };
                }

                return v;
              });

              return updatedViews;
            }
          );

          if (isFavoriteView) navigate("/duedates/views/assignedtome");
        },
      }
    );
  };

  const handleRename = (e) => {
    const name = e?.value?.trim();

    if (name !== view?.Name) {
      const updatedView = {
        Id: view?.Id,
        Name: name,
        IsFavorite: view?.IsFavorite,
        Order: view?.Order,
        UserId: UserId,
        BoardId: currentBoardId,
      };

      updateView(
        { view: updatedView },
        {
          onSuccess: (res) => {
            const { UpdatedEntity } = res;
            queryClient.invalidateQueries(["get-view", UpdatedEntity.Id]);
            queryClient.setQueryData(
              ["get-views", UpdatedEntity.BoardId],
              (old) => {
                const updatedViews = old?.map((v) => {
                  if (v.Id === UpdatedEntity.Id) {
                    return {
                      ...v,
                      Name: updatedView?.Name,
                    };
                  }

                  return v;
                });

                return updatedViews;
              }
            );
          },
          onError: () => {
            viewNameRef.current?.setValue(view?.Name);
          },
        }
      );
    }
  };

  const handleSelectMenuItem = (args) => {
    args?.item?.onClick?.();
  };

  const getDropdownOptions = () =>
    [
      {
        id: ids.viewOptions.rename,
        text: t("ViewHeader.Rename"),
        iconCss: "fa-regular fa-pencil",
        onClick: () => {
          // i think synfushion doesn't have a manual(react) way of focusing in the inplaceeditor input element
          const editBtn = document.querySelector(
            `#${ids.nameEditor} span.e-editable-overlay-icon`
          );
          editBtn?.click();
          setTimeout(() => {
            const nameInput = document.getElementById(
              `${ids.nameEditor}_editor`
            );
            nameInput?.focus();
          }, 100);
        },
      },
      {
        id: ids.viewOptions.delete,
        text: t("ViewHeader.Delete"),
        iconCss: "fa-regular fa-trash",
        onClick: () => {
          toggleModal(ModalEvents.DeleteViewConfirmationModal, view);
        },
      },
      {
        id: ids.viewOptions.favorite,
        text: view?.IsFavorite
          ? t("ViewHeader.Unfavorite")
          : t("ViewHeader.Favorite"),
        iconCss: view?.IsFavorite ? "fa-solid fa-star" : "fa-regular fa-star",
        onClick: handleFavorite,
      },
    ]?.filter(
      (x) =>
        (!!view && !view?.IsSystemView) || x.id === ids.viewOptions.favorite
    );

  const onInPlaceEditorValidation = (e) => {
    if (e.data.value == "Empty") {
      e.errorMessage = t("Shared:Required");
      return;
    }
  };

  if (!ready) return null;

  return (
    <div className="d-flex align-items-center mb-2">
      <i
        id={ids.favoriteButton}
        className={`ms-2 favorite-btn fs-1rem ${
          view?.IsFavorite ? "active fa-solid" : "fa-regular"
        } fa-star me-2`}
        onClick={handleFavorite}
      />

      <div className="title-view-name-container d-inline-block">
        {allowRenaming ? (
          <InPlaceHeaderEditor
            id={ids.nameEditor}
            ref={viewNameRef}
            value={view?.Name}
            name="Name"
            htmlAttributes={{
              maxlength: "100",
            }}
            validationRules={{
              Name: { required: true },
            }}
            validating={onInPlaceEditorValidation}
            actionSuccess={handleRename}
          />
        ) : (
          <span className="title-view-name">{viewName}</span>
        )}
      </div>
      <i className="title-separator" />
      <span className="title-row-counter me-2">
        {results} {t("ViewHeader.Results")}
      </span>
      <div className="d-inline-block ms-2">
        <IconDropDownButton
          id={ids.viewOptionsDropdown}
          select={handleSelectMenuItem}
          isTerciary
          iconCss="fa-solid fa-ellipsis-vertical"
          items={getDropdownOptions()}
        />
      </div>
    </div>
  );
};

export default ViewTitle;
